import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from 'react-query';

import { api } from '../../utils/api-client';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';

const SIMILAR_PROJECTS_TOP_K = 20;

type ErrorDto = {
  messages?: Record<string, string>;
  code?: string;
  retryAfterSeconds?: number;
  requestId?: string;
};

type SourceSimilarityMode = {
  mode: 'project';
  projectId?: string;
};

type QuerySimilarityMode = {
  mode: 'query';
  query?: string;
};

export type SimilaritySource = SourceSimilarityMode | QuerySimilarityMode;

export type SimilarProjectsFilters = {
  activeOrClosedAfter?: Dayjs | null;
};

export type SimilarProjectDto = {
  uuid: string;
  name: string;
  description: string;
  active: boolean;
  clientCvCount?: number | null;
  recruitmentCount?: number | null;
};

export type SimilarProjectHitDto = {
  score: number;
  project: SimilarProjectDto;
};

type SourceSimilarityRequestDto = {
  topK: number;
  filters?: {
    activeOrClosedAfter?: string;
  };
};

type QuerySimilarityRequestDto = {
  queryText: string;
  topK: number;
  filters?: {
    activeOrClosedAfter?: string;
  };
};

type UseGetSimilarProjectsProps = {
  source: SimilaritySource;
  filters?: SimilarProjectsFilters;
  enabled?: boolean;
};

export type SimilarProjectsApiError = {
  canRetry: boolean;
  kind: 'invalid-input' | 'source-not-found' | 'source-not-indexed' | 'service-unavailable' | 'rate-limited' | 'unknown';
  message: string;
  retryAfterSeconds?: number;
};

const buildFiltersPayload = (filters?: SimilarProjectsFilters) => {
  const activeOrClosedAfter = filters?.activeOrClosedAfter;
  if (!activeOrClosedAfter || !dayjs(activeOrClosedAfter).isValid()) {
    return undefined;
  }

  return {
    activeOrClosedAfter: dayjs(activeOrClosedAfter).format('YYYY-MM-DD'),
  };
};

export const buildSimilarProjectsRequestPayload = (
  source: SimilaritySource,
  filters?: SimilarProjectsFilters
): SourceSimilarityRequestDto | QuerySimilarityRequestDto => {
  const payloadFilters = buildFiltersPayload(filters);

  if (source.mode === 'project') {
    return {
      topK: SIMILAR_PROJECTS_TOP_K,
      ...(payloadFilters ? { filters: payloadFilters } : {}),
    };
  }

  return {
    queryText: source.query?.trim() ?? '',
    topK: SIMILAR_PROJECTS_TOP_K,
    ...(payloadFilters ? { filters: payloadFilters } : {}),
  };
};

export const mapSimilarProjectsApiError = (error: AxiosError<ErrorDto>): SimilarProjectsApiError => {
  const status = error.response?.status;
  const data = error.response?.data ?? {};
  const messages = data.messages ?? {};

  if (status === 400) {
    return {
      kind: 'invalid-input',
      message: 'Invalid input. Check query text/date.',
      canRetry: false,
    };
  }

  if (status === 404 && messages.project === 'not-found') {
    return {
      kind: 'source-not-found',
      message: 'Source project not found.',
      canRetry: false,
    };
  }

  if (status === 409 && messages.projectSimilarity === 'source-not-indexed') {
    return {
      kind: 'source-not-indexed',
      message: 'Similarity index is catching up. Try again in a moment.',
      canRetry: true,
    };
  }

  if (status === 503 && messages.projectSimilarity === 'SIMILARITY_UNAVAILABLE') {
    const requestId = data.requestId ?? error.response?.headers?.['x-request-id'];
    if (requestId) console.warn('[similarity] 503 requestId:', requestId);
    return {
      kind: 'service-unavailable',
      message: 'Similarity service unavailable. Try again later.',
      canRetry: false,
    };
  }

  if (status === 429 && messages.projectSimilarity === 'SIMILARITY_RERANK_RATE_LIMITED') {
    const retryAfterSeconds =
      data.retryAfterSeconds ??
      (error.response?.headers?.['retry-after'] ? Number(error.response.headers['retry-after']) : undefined);
    const requestId = data.requestId ?? error.response?.headers?.['x-request-id'];
    if (requestId) console.warn('[similarity] 429 requestId:', requestId);
    return {
      kind: 'rate-limited',
      message: retryAfterSeconds ? `Rate limited. Try again in ${retryAfterSeconds}s.` : 'Rate limited. Try again later.',
      canRetry: true,
      retryAfterSeconds,
    };
  }

  return {
    kind: 'unknown',
    message: 'Unable to fetch similar projects.',
    canRetry: false,
  };
};

const getSimilarityEndpoint = (source: SimilaritySource) => {
  if (source.mode === 'project') {
    return pathToUrl(ApiRoutesEnum.searchSimilarProjectsBySource, { uuid: source.projectId ?? '' });
  }

  return pathToUrl(ApiRoutesEnum.searchSimilarProjects);
};

export const useGetSimilarProjects = ({ source, filters, enabled = false }: UseGetSimilarProjectsProps) => {
  const normalizedSource: SimilaritySource =
    source.mode === 'query'
      ? {
          mode: 'query',
          query: source.query?.trim() ?? '',
        }
      : source;

  const queryKey =
    normalizedSource.mode === 'project'
      ? ['projects/similar', 'project', normalizedSource.projectId, filters?.activeOrClosedAfter?.toISOString()]
      : ['projects/similar', 'query', normalizedSource.query, filters?.activeOrClosedAfter?.toISOString()];

  return useQuery<SimilarProjectHitDto[], AxiosError<ErrorDto>>(
    queryKey,
    async () => {
      if (normalizedSource.mode === 'project' && !normalizedSource.projectId) {
        return [];
      }

      if (normalizedSource.mode === 'query' && !normalizedSource.query) {
        return [];
      }

      const endpoint = getSimilarityEndpoint(normalizedSource);
      const payload = buildSimilarProjectsRequestPayload(normalizedSource, filters);
      const response = await api.post<SimilarProjectHitDto[]>(endpoint, payload);

      return response.data ?? [];
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: false,
    }
  );
};
